/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import type { StyleProps } from './types.js'
import { cx, mergeStyle, pickStyleProps } from './types.js'

export interface ColorPickerProps extends StyleProps {
  /** Initial color value as a hex string, e.g. '#3b82f6'. Default: '#3b82f6' */
  value?: string
  /** Hidden input name for form submission. */
  name?: string
  /** Show alpha (opacity) slider. Default: false */
  withAlpha?: boolean
  class?: string
  style?: string | Record<string, string>
  [key: string]: unknown
}

function hsvToHex(h: number, s: number, v: number): string {
  s /= 100
  v /= 100
  const k = (n: number) => (n + h / 60) % 6
  const f = (n: number) => v - v * s * Math.max(0, Math.min(k(n), 4 - k(n), 1))
  const hex = (n: number) => Math.round(f(n) * 255).toString(16).padStart(2, '0')
  return `#${hex(5)}${hex(3)}${hex(1)}`
}

function hexToHsv(hex: string): [number, number, number] {
  const clean = hex.replace('#', '').slice(0, 6).padEnd(6, '0')
  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + 6) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
  }
  return [h, max === 0 ? 0 : (d / max) * 100, max * 100]
}

function isValidHex(v: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(v)
}

function hsvToRgbaStr(h: number, s: number, v: number, a: number): string {
  const hex = hsvToHex(h, s, v)
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${a.toFixed(2)})`
}

function ColorPicker({
  value: initialValue = '#3b82f6',
  name,
  withAlpha = false,
  class: className,
  style,
  ...rest
}: ColorPickerProps) {
  const initial = isValidHex(initialValue) ? initialValue : '#3b82f6'
  const [h, s, v0] = hexToHsv(initial)

  const [hue, setHue] = createSignal(h)
  const [sat, setSat] = createSignal(s)
  const [val, setVal] = createSignal(v0)
  const [alpha, setAlpha] = createSignal(1)
  const [hexInput, setHexInput] = createSignal(initial)

  const { styleObj, ownRest } = pickStyleProps(rest as Record<string, unknown>)

  let rootEl: HTMLElement | null = null

  const currentHex = () => hsvToHex(hue(), sat(), val())

  function dispatchChange(hex: string) {
    if (!rootEl) return
    rootEl.dispatchEvent(
      new CustomEvent('dv-color-change', {
        detail: { color: hex, alpha: alpha() },
        bubbles: true,
      }),
    )
  }

  function syncFromHsv() {
    const hex = hsvToHex(hue(), sat(), val())
    setHexInput(hex)
    dispatchChange(hex)
  }

  // 2D color area (saturation × value)
  function trackArea(e: PointerEvent) {
    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height))
    setSat(x * 100)
    setVal((1 - y) * 100)
    syncFromHsv()
  }

  // Hue slider
  function trackHue(e: PointerEvent) {
    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    setHue(x * 360)
    syncFromHsv()
  }

  // Alpha slider
  function trackAlpha(e: PointerEvent) {
    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    setAlpha(x)
    dispatchChange(currentHex())
  }

  function handleHexInput(e: Event) {
    const raw = (e.target as HTMLInputElement).value
    setHexInput(raw)
    const full = raw.startsWith('#') ? raw : `#${raw}`
    if (isValidHex(full)) {
      const [nh, ns, nv] = hexToHsv(full)
      setHue(nh)
      setSat(ns)
      setVal(nv)
      dispatchChange(full)
    }
  }

  return (
    <div
      class={cx('dv-color-picker', className)}
      style={mergeStyle(styleObj, style)}
      ref={(el: Element) => { rootEl = el as HTMLElement }}
      {...ownRest}
    >
      {name && <input type='hidden' name={name} value={() => withAlpha ? hsvToRgbaStr(hue(), sat(), val(), alpha()) : currentHex()} />}

      {/* 2D saturation × value area */}
      <div
        class='dv-color-picker-area'
        style={() => `background-color: hsl(${hue()}, 100%, 50%)`}
        onPointerDown={(e: PointerEvent) => {
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
          trackArea(e)
        }}
        onPointerMove={(e: PointerEvent) => {
          if (e.buttons === 0) return
          trackArea(e)
        }}
      >
        <div
          class='dv-color-picker-area-thumb'
          style={() => `left: ${sat()}%; top: ${100 - val()}%`}
        />
      </div>

      <div class='dv-color-picker-sliders'>
        {/* Hue slider */}
        <div
          class='dv-color-picker-slider dv-color-picker-hue-slider'
          onPointerDown={(e: PointerEvent) => {
            ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
            trackHue(e)
          }}
          onPointerMove={(e: PointerEvent) => {
            if (e.buttons === 0) return
            trackHue(e)
          }}
        >
          <div
            class='dv-color-picker-slider-thumb'
            style={() => `left: ${(hue() / 360) * 100}%`}
          />
        </div>

        {/* Alpha slider */}
        {withAlpha && (
          <div
            class='dv-color-picker-slider dv-color-picker-alpha-slider'
            style={() => `--dv-cp-hex: ${currentHex()}`}
            onPointerDown={(e: PointerEvent) => {
              ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
              trackAlpha(e)
            }}
            onPointerMove={(e: PointerEvent) => {
              if (e.buttons === 0) return
              trackAlpha(e)
            }}
          >
            <div
              class='dv-color-picker-slider-thumb'
              style={() => `left: ${alpha() * 100}%`}
            />
          </div>
        )}
      </div>

      <div class='dv-color-picker-footer'>
        <div
          class='dv-color-picker-preview'
          style={() => withAlpha
            ? `background: ${hsvToRgbaStr(hue(), sat(), val(), alpha())}`
            : `background: ${currentHex()}`
          }
          aria-hidden='true'
        />
        <input
          class='dv-color-picker-hex-input'
          type='text'
          maxLength={7}
          value={() => hexInput()}
          onInput={handleHexInput}
          aria-label='Hex color value'
          spellcheck={false}
        />
        {withAlpha && (
          <input
            class='dv-color-picker-alpha-input'
            type='number'
            min='0'
            max='100'
            step='1'
            value={() => Math.round(alpha() * 100)}
            onInput={(e: Event) => {
              const pct = Number((e.target as HTMLInputElement).value)
              if (!Number.isNaN(pct)) setAlpha(Math.max(0, Math.min(1, pct / 100)))
            }}
            aria-label='Opacity %'
          />
        )}
      </div>
    </div>
  )
}

export default reactive(ColorPicker)
